import React from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

const DISCIPLINES = [
  'GAMEPLAY PROGRAMMING',
  'ENEMY AI',
  'WEAPON & COMBAT MECHANICS',
  'LEVEL & GAME DESIGN',
  'UI DESIGN',
  'SHADERS & VFX',
  'QA & TESTING',
  'TEAM & PROJECT MANAGEMENT',
  'OOP',
  'SOLID PRINCIPLES',
  'CLEAN CODE',
  'DESIGN PATTERNS',
  'SOFTWARE ARCHITECTURE',
  'REFACTORING & DEBUGGING',
];

const Skills = () => {
  const { t } = useTranslation();

  const loadoutCards = [
    {
      title: t('skills.engines'),
      items: ['Unity', 'Unreal Engine 5'],
      accent: 'text-arcade-cyan',
      border: 'border-arcade-cyan',
      glow: 'shadow-[0_0_14px_rgba(77,232,244,0.25)]',
    },
    {
      title: t('skills.languages'),
      items: ['C#', 'C++ & Blueprints'],
      accent: 'text-arcade-pink',
      border: 'border-arcade-pink',
      glow: 'shadow-[0_0_14px_rgba(255,46,126,0.25)]',
    },
    {
      title: t('skills.pipeline'),
      items: ['Version Control (Git)', 'itch.io & Steam release'],
      accent: 'text-arcade-yellow',
      border: 'border-arcade-yellow',
      glow: 'shadow-[0_0_14px_rgba(255,201,61,0.25)]',
    },
    {
      title: t('skills.tools').toUpperCase(),
      items: ['GitHub · Plastic SCM', 'Rider · Visual Studio · Blender'],
      accent: 'text-arcade-green',
      border: 'border-arcade-green',
      glow: 'shadow-[0_0_14px_rgba(77,244,138,0.25)]',
    },
  ];

  return (
    <section
      id="skills"
      className="min-h-screen relative overflow-hidden bg-arcade-bg crt-scanlines flex items-center"
    >
      {/* faint grid */}
      <div className="absolute inset-0 bg-grid-pattern bg-grid opacity-[0.06] pointer-events-none"></div>

      {/* decorative pixels */}
      <div className="absolute top-[12%] right-[14%] w-3 h-3 bg-arcade-pink"></div>
      <div className="absolute top-[38%] left-[5%] w-2 h-2 bg-arcade-green"></div>
      <div className="absolute bottom-[14%] right-[8%] w-2.5 h-2.5 bg-arcade-yellow"></div>
      <div className="absolute bottom-[30%] left-[12%] w-2 h-2 bg-arcade-cyan animate-pulse"></div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-20 w-full">
        {/* section header */}
        <motion.div
          className="flex items-center gap-6 mb-4"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <h2 className="font-pixel text-arcade-yellow text-base md:text-xl whitespace-nowrap">
            {t('skills.loadout')}
          </h2>
          <div className="h-[2px] flex-1 bg-gradient-to-r from-arcade-yellow to-arcade-yellow/0"></div>
        </motion.div>

        <motion.h3
          className="font-pixel text-arcade-cyan text-2xl md:text-5xl mb-10 md:mb-14 drop-shadow-[0_0_12px_rgba(77,232,244,0.45)]"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.1 }}
        >
          {t('skills.equipped')}
          <span className="inline-block w-3 h-6 md:w-4 md:h-10 bg-arcade-cyan ml-3 align-middle animate-pulse"></span>
        </motion.h3>

        {/* loadout cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6 mb-12 md:mb-16">
          {loadoutCards.map((card, index) => (
            <motion.div
              key={card.title}
              className={`group relative border-2 ${card.border} ${card.glow} rounded-lg bg-arcade-panel/60 px-6 py-6 transition-transform duration-200 hover:-translate-y-1`}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.15 + index * 0.1 }}
            >
              <span className={`absolute top-2 right-3 font-pixel text-[8px] ${card.accent} opacity-0 group-hover:opacity-100 transition-opacity`}>
                ▶ EQUIPPED
              </span>
              <h4 className={`font-pixel ${card.accent} text-sm md:text-lg mb-5`}>
                {card.title}
              </h4>
              <ul className="space-y-3">
                {card.items.map((item) => (
                  <li key={item} className="font-mono text-sm md:text-base text-gray-200">
                    {item}
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>

        {/* disciplines */}
        <motion.h4
          className="font-pixel text-arcade-purple text-sm md:text-lg mb-6"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.2 }}
        >
          {t('skills.disciplines')}
        </motion.h4>

        <motion.div
          className="flex flex-wrap gap-3"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.3 }}
        >
          {DISCIPLINES.map((item) => (
            <span
              key={item}
              className="font-mono text-xs md:text-sm text-arcade-cyan border border-arcade-cyan/60 bg-arcade-panel/40 px-4 py-2 tracking-wider hover:bg-arcade-cyan/10 transition-colors"
            >
              {item}
            </span>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default Skills;
